<template>
  <div class="access">
    <!-- Loading state -->
    <div v-if="isCheckingAuth" class="access__loading">
      <div class="spinner"></div>
      <p class="loading-text">Loading...</p>
    </div>

    <!-- Login form -->
    <div v-else class="access__card">
      <div class="access-header">
        <img src="/logo-dark-transparent.png" class="access-header__logo" width="150" alt="">
        <h3 class="access-header__title">Welcome!</h3>
        <div class="access-header__description">
          To get started, please enter the email you used during registration. You'll then be able to select your event
          and access your personalized Attendee Dashboard.
        </div>
      </div>

      <!-- SEND MAIL -->
      <div v-if="step === 'fill-email'">
        <div class="access-stepper">
          <stepper
            :active-index="0"
            :steps="steps"
            @stepClick="stepClick"
          />
        </div>

        <validation-observer v-slot="{ invalid, valid, errors, ...rest }" ref="form">
          <form @submit.prevent="handleAuthorizeUserByEmail">
            <validation-provider vid="email" rules="required" v-slot="{ errors }">
              <c-input
                label="Email"
                type="email"
                help="Enter the email you used to register for your event."
                v-model="email"
                :error="errors[0]"
              ></c-input>
            </validation-provider>
            <div class="access__button">
              <c-button
                type="submit"
                theme="success"
                icon="fa-arrow-right"
                fill
                uppercase
                :show-indicator="showIndicator"
              >Next</c-button>
            </div>
          </form>
        </validation-observer>
      </div>

      <!-- CONFIRM -->
      <div v-if="step === 'confirm'">
        <div class="access-stepper">
          <stepper
            :active-index="1"
            :steps="steps"
            @stepClick="stepClick"
          />
        </div>

        <validation-observer v-slot="{ invalid, valid, errors, ...rest }" ref="form">
          <form @submit.prevent="handleConfirmSubmit">
            <validation-provider vid="code" rules="required" v-slot="{ errors }">
              <c-input
                label="Code"
                name="code"
                help="Please check your email and enter the code to confirm your registration."
                @input="onCodeInput"
                v-model="code"
                :error="errors[0]"
              ></c-input>
            </validation-provider>

            <div class="access__button">
              <c-button
                type="submit"
                theme="success"
                icon="fa-arrow-right"
                fill
                uppercase
                :show-indicator="showIndicator"
              >Next</c-button>
            </div>
          </form>
        </validation-observer>
      </div>
    </div>
  </div>
</template>

<script>
import accessAPI from "../../api/accessAPI";
import Stepper from "../../components/ui/Stepper";
import { setHeaderAuth } from "../../api";
import { storage } from "../../helpers/storage";

export default {
  components: {
    Stepper,
  },
  data() {
    return {
      email: '',
      code: '',
      showIndicator: false,
      attendeeId: this.$route.params.attendeeId,
      step: 'fill-email',
      steps: [
        {name: 'Enter Email', alias: 'fill-email', model: 'email'},
        {name: 'Confirm Code', alias: 'confirm', mode: 'code'}
      ],
      userId: null,
      isCheckingAuth: true,
    }
  },
  async mounted() {
    console.log('[Access] Component mounted');
    try {
      // Чекаємо на готовність Median library
      await this.waitForMedianReady();

      // Перевіряємо чи є збережений токен та attendee_id
      await this.checkExistingAuth();
    } catch (error) {
      console.error('[Access] Mount error:', error);
      this.isCheckingAuth = false;
    }
  },
  methods: {
    async waitForMedianReady() {
      // Якщо Median не доступний - одразу resolve (для десктопу)
      if (typeof window === 'undefined' || !window.median) {
        console.log('[Access] Median not available - using localStorage only');
        return Promise.resolve();
      }

      // Якщо Median вже готова - одразу resolve
      if (window.medianLibraryReady || (window.median.storage && window.median.storage.app)) {
        console.log('[Access] Median already ready');
        return Promise.resolve();
      }

      // Чекаємо максимум 3 секунди на ініціалізацію
      console.log('[Access] Waiting for Median...');
      return new Promise((resolve) => {
        let attempts = 0;
        const maxAttempts = 30;

        const checkReady = () => {
          attempts++;

          if (window.medianLibraryReady || (window.median && window.median.storage && window.median.storage.app)) {
            console.log('[Access] Median ready after', attempts * 100, 'ms');
            resolve();
          } else if (attempts >= maxAttempts) {
            console.log('[Access] Median timeout - continuing without it');
            resolve();
          } else {
            setTimeout(checkReady, 100);
          }
        };

        checkReady();
      });
    },

    async checkExistingAuth() {
      console.log('[Access] Checking existing auth...');

      try {
        const param = new URLSearchParams(window.location.search);
        const tokenFromUrl = param.get('token');

        console.log('[Access] Token from URL:', !!tokenFromUrl);

        // Витягуємо токен з storage
        const storedToken = await storage.getItem('token');
        console.log('[Access] Token from storage:', !!storedToken);

        const token = tokenFromUrl || storedToken;

        if (!token) {
          console.log('[Access] No token found - showing login form');
          this.isCheckingAuth = false;
          return;
        }

        console.log('[Access] Token found, validating...');

        // Встановлюємо токен в headers
        await setHeaderAuth(token);

        // Зберігаємо токен якщо він з URL
        if (tokenFromUrl) {
          console.log('[Access] Saving URL token to storage');
          await storage.setItem('token', tokenFromUrl);
        }

        // Пробуємо завантажити дані користувача для валідації токена
        try {
          const attendeeIdFromService = await storage.getItem('attendee_id');
          let actualAttendeeId = this.attendeeId || attendeeIdFromService;

          console.log('[Access] AttendeeId from route:', this.attendeeId);
          console.log('[Access] AttendeeId from storage:', attendeeIdFromService);
          console.log('[Access] Actual AttendeeId:', actualAttendeeId);

          // Якщо немає attendeeId або є новий токен з URL - йдемо на Event Switcher
          if (!actualAttendeeId || tokenFromUrl) {
            console.log('[Access] Redirecting to Event Switcher');

            // ВАЖЛИВО: спочатку виключаємо прапорець, потім редірект
            this.isCheckingAuth = false;

            await this.$nextTick();

            this.$router.push({
              name: 'Guest Event Switcher',
            });
            return;
          }

          // Завантажуємо дані attendee
          console.log('[Access] Loading attendee data...');
          await this.$store.dispatch("attendeeStore/fetchAttendee", {
            id: actualAttendeeId
          });

          console.log('[Access] Attendee loaded, redirecting to dashboard');

          // ВАЖЛИВО: спочатку виключаємо прапорець, потім редірект
          this.isCheckingAuth = false;

          await this.$nextTick();

          this.$router.push({
            name: 'Full Schedule',
            params: {
              attendeeId: actualAttendeeId,
            }
          });
        } catch (error) {
          // Токен невалідний
          console.error('[Access] Token validation failed:', error);
          console.log('[Access] Clearing invalid token');

          await storage.removeItem('token');
          await storage.removeItem('attendee_id');

          this.isCheckingAuth = false;
        }
      } catch (error) {
        console.error('[Access] Auth check error:', error);
        this.isCheckingAuth = false;
      }
    },

    onCodeInput(val) {
      this.code = (val || '').toUpperCase();
    },

    stepClick({index, step}){
      if (this[step.model]) {
        this.step = step.alias;
      }
    },

    async handleConfirmSubmit() {
      const isValid = await this.$refs.form.validate();

      if (!isValid) {
        return;
      }

      this.showIndicator = true;

      const data = {
        email: this.email,
        user_id: this.userId,
        code: (this.code || '').toUpperCase(),
      };

      try {
        console.log('[Access] Confirming code...');
        const res = await accessAPI.confirmUserByCode(data);

        const token = res.data.data.token;
        console.log('[Access] Code confirmed, token received');

        // Зберігаємо токен
        await storage.setItem('token', token);
        await setHeaderAuth(token);

        console.log('[Access] Token saved, redirecting...');

        this.$router.push({
          name: 'Guest Event Switcher',
        });
      } catch (error) {
        console.error('[Access] Confirm error:', error);
        if (error.response?.data?.errors) {
          this.$refs.form.setErrors(error.response?.data?.errors);
        } else {
          this.$refs.form.setErrors({
            code: ['Invalid code. Something Went Wrong. Please use link from the email.']
          });
        }
      } finally {
        this.showIndicator = false;
      }
    },

    async handleAuthorizeUserByEmail() {
      const isValid = await this.$refs.form.validate();

      if (!isValid) {
        return;
      }

      this.showIndicator = true;

      const data = {
        email: this.email,
      };

      try {
        console.log('[Access] Authorizing by email...');
        const res = await accessAPI.authorizeUserByEmail(data);

        this.userId = res.data.data.user_id;
        this.step = 'confirm';

        console.log('[Access] Email sent, showing code input');

        this.$nextTick(() => {
          this.$refs.form.reset();
        });
      } catch (error) {
        console.error('[Access] Authorize error:', error);
        if (error?.response?.data?.errors) {
          this.$refs.form.setErrors(error.response.data.errors);
        }
      } finally {
        this.showIndicator = false;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.access-header {
  max-width: 600px;
  margin: 0 auto;
  padding: 0 var(--attendee-gap-md);
  text-align: center;

  &__logo {
    margin-bottom: var(--attendee-gap-md);

    @media (max-width: 900px) {
      margin-bottom: 60px;
    }
  }

  &__description {
    font-size: 16px;
    color: var(--attendee-text-primary-color);
    max-width: 500px;
    margin:  0 auto var(--attendee-gap-md);

    @media (max-width: 900px) {
      margin-bottom: 60px;
    }
  }

  &__title {
    font-size: 22px;
    color: var(--attendee-text-primary-color);
    margin-bottom: var(--attendee-gap-md);
  }
}

.access-stepper {
  margin-bottom: var(--attendee-gap-lg);
}

.access {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  padding: var(--attendee-gap-md);

  &__loading {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
  }

  &__button {
    text-align: right;
    margin-top: var(--attendee-gap-lg);

    button {
      touch-action: manipulation;
      -webkit-tap-highlight-color: transparent;
      user-select: none;
    }
  }

  &__card {
    max-width: 800px;
    width: 100%;
    padding: var(--attendee-gap-lg);
    border: 1px solid var(--attendee-border-color);
    border-radius: var(--attendee-card-border-radius);
    background-color: var(--attendee-card-color);

    @media (max-width: 900px) {
      max-height: 100vh;
      overflow-y: auto;
    }
  }
}

.loading-text {
  color: var(--attendee-text-primary-color);
  font-size: 16px;
  margin: 0;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: var(--attendee-primary-color, #3498db);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
